import React, { useContext, useEffect, useMemo, useState } from "react";
import {
    Container,
    Typography,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    Grid,
    Stack,
    Avatar,
    useTheme,
    Box,
    Skeleton,
    Chip,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_URL, buildFetchUrl } from "../../config";
import { UserRoleContext } from "../../GetUserRole";
import {
    QrCodeScanner,
    Description,
    Edit,
    Link as LinkIcon,
    Delete,
    Language,
    ExpandMore,
} from "@mui/icons-material";

const BusinessListPage = () => {
    const theme = useTheme();
    const [restaurants, setRestaurants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [recordToDelete, setRecordToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [expandedRecordId, setExpandedRecordId] = useState(false);
    const navigate = useNavigate();
    const { role } = useContext(UserRoleContext);

    const canManageRecords = useMemo(() => [1, 2, 3].includes(Number(role)), [role]);


    const sortedRestaurants = useMemo(() => {
        const priority = (record) => {
            const categoryId = Number(record.CategoryID);
            if (categoryId === 3) return 0;
            if (categoryId === 4) return 1;
            return 2;
        };

        return [...restaurants].sort((a, b) => {
            const priorityDiff = priority(a) - priority(b);
            if (priorityDiff !== 0) {
                return priorityDiff;
            }

            return String(a.name || "").localeCompare(String(b.name || ""));
        });
    }, [restaurants]);

    const stats = useMemo(() => {
        const websiteCount = restaurants.filter((item) => Number(item.CategoryID) === 3).length;
        const surveyCount = restaurants.filter((item) => Number(item.CategoryID) === 4).length;
        const urlCount = restaurants.filter((item) => Number(item.CategoryID) === 2).length;
        const fileCount = restaurants.filter((item) => ![2, 3, 4].includes(Number(item.CategoryID))).length;

        return [
            { label: "Total records", value: restaurants.length, tone: "rgba(91, 95, 246, 0.14)", color: "#4c54f0" },
            { label: "Websites", value: websiteCount, tone: "rgba(96, 165, 250, 0.16)", color: "#0f7ae5" },
            { label: "Surveys", value: surveyCount, tone: "rgba(34, 197, 94, 0.16)", color: "#0f9d58" },
            { label: "URLs", value: urlCount, tone: "rgba(168, 85, 247, 0.16)", color: "#8b3df0" },
            { label: "Files", value: fileCount, tone: "rgba(45, 212, 191, 0.18)", color: "#0f9d89" },
        ];
    }, [restaurants]);

    const getCategoryLabel = (categoryId) => {
        if (Number(categoryId) === 2) return "URL";
        if (Number(categoryId) === 3) return "Website";
        if (Number(categoryId) === 4) return "Survey";
        return "File";
    };

    useEffect(() => {
        const fetchRestaurants = async () => {
            try {
                const response = await axios.get(API_URL + "get_restaurants.php", {
                    withCredentials: true,
                });
                const records = Array.isArray(response.data) ? response.data : [];
                setRestaurants(records);
                setExpandedRecordId((prev) => (prev && records.some((record) => record.id === prev) ? prev : records[0]?.id || false));
            } catch (error) {
                console.error("Error fetching restaurants:", error);
                setErrorMessage("Failed to load records. Please try again.");
            } finally {
                setLoading(false);
            }
        };
        fetchRestaurants();
    }, []);

    const handleDeleteRecord = async () => {
        if (!recordToDelete) {
            return;
        }


        setDeleteLoading(true);
        try {
            const response = await axios.post(
                API_URL + "delete_record.php",
                { data: { id: recordToDelete.id } },
                { withCredentials: true }
            );

            setRestaurants((prev) => prev.filter((restaurant) => restaurant.id !== recordToDelete.id));
            setExpandedRecordId((prev) => (prev === recordToDelete.id ? false : prev));
            setSuccessMessage(response.data?.message || "Record deleted successfully.");
            setRecordToDelete(null);
        } catch (error) {
            console.error("Error deleting record:", error);
            setErrorMessage(error.response?.data?.message || "Failed to delete the record.");
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <Container
            maxWidth="md"
            sx={{
                py: { xs: 3, md: 6 },
                minHeight: "100vh",
                px: { xs: 2, sm: 3 },
                position: "relative",
            }}
        >
            <Box
                sx={{
                    mb: { xs: 3, sm: 4 },
                    p: { xs: 2.5, sm: 3.5 },
                    borderRadius: { xs: 4, md: 5 },
                    background: "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 244, 255, 0.92) 100%)",
                    border: "1px solid rgba(116, 129, 171, 0.18)",
                    boxShadow: "0 28px 60px rgba(31, 41, 55, 0.10)",
                    backdropFilter: "blur(10px)",
                }}
            >
                <Typography
                    variant="h3"
                    sx={{
                        mb: 1,
                        textAlign: "center",
                        fontWeight: 800,
                        color: "primary.main",
                        letterSpacing: "-0.02em",
                        [theme.breakpoints.down("sm")]: { fontSize: "2rem" },
                        background: "linear-gradient(90deg, #4d7cff 0%, #9153ff 55%, #30b5ff 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                    }}
                >
                    My Records
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        textAlign: "center",
                        color: "text.secondary",
                        maxWidth: 620,
                        mx: "auto",
                        mb: 3,
                    }}
                >
                    Clean list view up top, with each record expanding only when you want the full details and actions.
                </Typography>

                <Grid container spacing={1.5}>
                    {stats.map((stat) => (
                        <Grid item xs={6} md={3} key={stat.label}>
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 3,
                                    backgroundColor: stat.tone,
                                    border: "1px solid rgba(116, 129, 171, 0.12)",
                                    height: "100%",
                                }}
                            >
                                <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: "0.08em" }}>
                                    {stat.label}
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: stat.color }}>
                                    {stat.value}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {loading ? (
                <Grid container spacing={2}>
                    {[1, 2, 3, 4].map((item) => (
                        <Grid item xs={12} key={item}>
                            <Skeleton variant="rounded" width="100%" height={84} animation="wave" />
                        </Grid>
                    ))}
                </Grid>
            ) : sortedRestaurants.length === 0 ? (
                <Alert
                    severity="info"
                    sx={{
                        borderRadius: 3,
                        boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)",
                    }}
                >
                    No records available yet. Create a file, URL, website, or survey record to get started.
                </Alert>
            ) : (
                <Stack spacing={1.5}>
                    {sortedRestaurants.map((restaurant, index) => {
                        const isExpanded = expandedRecordId === restaurant.id;
                        const categoryLabel = getCategoryLabel(restaurant.CategoryID);

                        return (
                            <Accordion
                                key={restaurant.id}
                                expanded={isExpanded}
                                onChange={(_, expanded) => setExpandedRecordId(expanded ? restaurant.id : false)}
                                disableGutters
                                elevation={0}
                                sx={{
                                    overflow: "hidden",
                                    borderRadius: "24px !important",
                                    border: isExpanded ? "1px solid rgba(91, 95, 246, 0.34)" : "1px solid rgba(116, 129, 171, 0.16)",
                                    background: isExpanded
                                        ? "linear-gradient(135deg, rgba(248, 250, 255, 0.98) 0%, rgba(241, 245, 255, 0.96) 100%)"
                                        : "linear-gradient(135deg, rgba(255, 255, 255, 0.94) 0%, rgba(249, 251, 255, 0.92) 100%)",
                                    boxShadow: isExpanded
                                        ? "0 22px 44px rgba(79, 70, 229, 0.16)"
                                        : "0 12px 28px rgba(15, 23, 42, 0.07)",
                                    transition: "border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease",
                                    "&:before": { display: "none" },
                                    "&:hover": {
                                        transform: "translateY(-2px)",
                                        boxShadow: "0 18px 34px rgba(79, 70, 229, 0.14)",
                                    },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMore sx={{ color: isExpanded ? "#4f46e5" : "#64748b" }} />}
                                    sx={{
                                        px: { xs: 2, sm: 2.5 },
                                        py: 0.75,
                                        minHeight: 76,
                                        "& .MuiAccordionSummary-content": {
                                            my: 1.25,
                                        },
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1.5}
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{ width: "100%", pr: 1 }}
                                    >
                                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: isExpanded ? "rgba(79, 70, 229, 0.16)" : "rgba(77, 124, 255, 0.12)",
                                                    color: isExpanded ? "#4f46e5" : "#4357f5",
                                                    width: 46,
                                                    height: 46,
                                                    fontSize: "1.1rem",
                                                    fontWeight: 700,
                                                    boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.7)",
                                                }}
                                            >
                                                {restaurant.name?.charAt(0)?.toUpperCase() || "R"}
                                            </Avatar>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: { xs: "1rem", sm: "1.1rem" },
                                                        color: "#0f172a",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {restaurant.name || `Record ${index + 1}`}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                                    Tap to {isExpanded ? "hide" : "show"} record details
                                                </Typography>
                                            </Box>
                                        </Stack>

                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ display: { xs: "none", sm: "flex" } }}>
                                            <Chip
                                                label={categoryLabel}
                                                size="small"
                                                sx={{
                                                    fontWeight: 700,
                                                    borderRadius: "999px",
                                                    background: isExpanded ? "rgba(91, 95, 246, 0.14)" : "rgba(148, 163, 184, 0.14)",
                                                    color: isExpanded ? "#4d65f3" : "#475569",
                                                }}
                                            />
                                            <Chip
                                                label={`#${restaurant.base36_id}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                    fontWeight: 600,
                                                    borderRadius: "999px",
                                                    borderColor: "rgba(100, 116, 139, 0.26)",
                                                    color: "#475569",
                                                }}
                                            />
                                        </Stack>
                                    </Stack>
                                </AccordionSummary>

                                <AccordionDetails sx={{ px: { xs: 2, sm: 2.5 }, pb: { xs: 2.25, sm: 2.5 }, pt: 0 }}>
                                    <Grid container spacing={1.5} sx={{ mb: 2.25 }}>
                                        <Grid item xs={12} sm={4}>
                                            <Box sx={{ p: 1.5, borderRadius: 3, background: "rgba(255, 255, 255, 0.82)", border: "1px solid rgba(148, 163, 184, 0.18)" }}>
                                                <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                                    Record name
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.5 }}>
                                                    {restaurant.name || "Untitled record"}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box sx={{ p: 1.5, borderRadius: 3, background: "rgba(255, 255, 255, 0.82)", border: "1px solid rgba(148, 163, 184, 0.18)" }}>
                                                <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                                    Type
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.5 }}>
                                                    {categoryLabel}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box sx={{ p: 1.5, borderRadius: 3, background: "rgba(255, 255, 255, 0.82)", border: "1px solid rgba(148, 163, 184, 0.18)" }}>
                                                <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                                    Record ID
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.5 }}>
                                                    {restaurant.base36_id}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 1.25,
                                            justifyContent: { xs: "stretch", sm: "flex-start" },
                                        }}
                                    >
                                        <Button
                                            variant="contained"
                                            startIcon={<QrCodeScanner />}
                                            onClick={() => navigate(`/view-qr/${restaurant.base36_id}`)}
                                            sx={{
                                                flex: { xs: "1 1 100%", sm: "0 1 auto" },
                                                width: { xs: "100%", sm: "auto" },
                                                minWidth: { sm: 155 },
                                                borderRadius: "12px",
                                                textTransform: "none",
                                                fontWeight: 700,
                                                px: 2.4,
                                                py: 1,
                                                background: "linear-gradient(135deg, #5B5FF6 0%, #4A9FE8 100%)",
                                            }}
                                        >
                                            View QR
                                        </Button>

                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            startIcon={Number(restaurant.CategoryID) === 2 ? <LinkIcon /> : Number(restaurant.CategoryID) === 3 ? <Language /> : <Description />}
                                            onClick={Number(restaurant.CategoryID) !== 3 ? () => {
                                                window.location.href = buildFetchUrl(restaurant.base36_id);
                                            } : () => {
                                                window.location.href = restaurant.menu_link || `/client/${restaurant.business}`;
                                            }}
                                            sx={{
                                                flex: { xs: "1 1 100%", sm: "0 1 auto" },
                                                width: { xs: "100%", sm: "auto" },
                                                minWidth: { sm: 155 },
                                                borderRadius: "12px",
                                                textTransform: "none",
                                                fontWeight: 700,
                                                px: 2.4,
                                                py: 1,
                                                background: "linear-gradient(135deg, #6E45E2 0%, #53A0FD 100%)",
                                            }}
                                        >
                                            {Number(restaurant.CategoryID) === 2
                                                ? "Open URL"
                                                : Number(restaurant.CategoryID) === 3
                                                    ? "Open Website"
                                                    : Number(restaurant.CategoryID) === 4
                                                        ? "Open Survey"
                                                        : "Open File"}
                                        </Button>

                                        {canManageRecords && (
                                            <Button
                                                variant="outlined"
                                                color="inherit"
                                                startIcon={<Edit />}
                                                onClick={() => navigate(`/edit-menu/${restaurant.id}/${restaurant.CategoryID}`)}
                                                sx={{
                                                    flex: { xs: "1 1 100%", sm: "0 1 auto" },
                                                    width: { xs: "100%", sm: "auto" },
                                                    minWidth: { sm: 155 },
                                                    borderRadius: "12px",
                                                    textTransform: "none",
                                                    fontWeight: 700,
                                                    borderColor: "rgba(76, 92, 130, 0.35)",
                                                    color: "#223",
                                                }}
                                            >
                                                {Number(restaurant.CategoryID) === 2
                                                    ? "Edit URL"
                                                    : Number(restaurant.CategoryID) === 3
                                                        ? "Edit Website"
                                                        : Number(restaurant.CategoryID) === 4
                                                            ? "Edit Survey"
                                                            : "Edit File"}
                                            </Button>
                                        )}


                                        {Number(restaurant.CategoryID) === 4 && (
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                startIcon={<Description />}
                                                onClick={() => navigate(`/edit-menu/${restaurant.id}/${restaurant.CategoryID}?view=responses`)}
                                                sx={{
                                                    flex: { xs: "1 1 100%", sm: "0 1 auto" },
                                                    width: { xs: "100%", sm: "auto" },
                                                    minWidth: { sm: 170 },
                                                    borderRadius: "12px",
                                                    textTransform: "none",
                                                    fontWeight: 700,
                                                }}
                                            >
                                                View Responses
                                            </Button>
                                        )}

                                        {canManageRecords && (
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                startIcon={<Delete />}
                                                onClick={() => setRecordToDelete(restaurant)}
                                                sx={{
                                                    flex: { xs: "1 1 100%", sm: "0 1 auto" },
                                                    width: { xs: "100%", sm: "auto" },
                                                    minWidth: { sm: 155 },
                                                    borderRadius: "12px",
                                                    textTransform: "none",
                                                    fontWeight: 700,
                                                }}
                                            >
                                                Delete Record
                                            </Button>
                                        )}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Stack>
            )}

            <Dialog open={!!recordToDelete} onClose={() => !deleteLoading && setRecordToDelete(null)} fullWidth maxWidth="xs">
                <DialogTitle>Delete Record</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        Delete {recordToDelete?.name || "this record"}? This also removes its QR image and any locally stored file.
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setRecordToDelete(null)} disabled={deleteLoading}>Cancel</Button>
                    <Button onClick={handleDeleteRecord} color="error" variant="contained" disabled={deleteLoading}>
                        {deleteLoading ? <CircularProgress size={20} color="inherit" /> : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={!!errorMessage} autoHideDuration={4000} onClose={() => setErrorMessage("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                <Alert severity="error" variant="filled" onClose={() => setErrorMessage("")} sx={{ width: "100%" }}>
                    {errorMessage}
                </Alert>
            </Snackbar>

            <Snackbar open={!!successMessage} autoHideDuration={4000} onClose={() => setSuccessMessage("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                <Alert severity="success" variant="filled" onClose={() => setSuccessMessage("")} sx={{ width: "100%" }}>
                    {successMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default BusinessListPage;
